import type { AudioItem } from '@listen-draw/shared';
import { LETTERS, RECORDINGS } from '@listen-draw/shared';
import { delay, playRecording, prepareRecordings, recordingDuration } from './sound.js';

export interface PlayOptions {
  items: AudioItem[];
  rate: number;
  gapMs: number;
  /** Host keeps the recorded timing when sound comes from participants' devices. */
  speak: boolean;
  voiceURI?: string | null;
  onItemStart?: (letterIndex: number) => void;
  onDone?: () => void;
  onError?: (error: Error) => void;
}
export const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
export function listEnglishVoices(): SpeechSynthesisVoice[] {
  return ttsSupported ? speechSynthesis.getVoices().filter((v) => /^en([-_]|$)/i.test(v.lang)) : [];
}
export function pickVoice(voiceURI?: string | null): SpeechSynthesisVoice | null {
  const voices = listEnglishVoices();
  return (
    voices.find((v) => v.voiceURI === voiceURI) ??
    voices.find((v) => /^en[-_]US/i.test(v.lang)) ??
    voices[0] ??
    null
  );
}
export function estimateMs(text: string, rate: number): number {
  return Math.round(
    recordingDuration(text, rate) ?? (900 + text.length * 62) / Math.max(0.6, rate),
  );
}

/** Bundled recordings first; device TTS is only used for new AI-generated text. */
export class Player {
  private controller = new AbortController();
  constructor(private opts: PlayOptions) {}
  start(): void {
    void this.run().catch((error) => {
      if (!this.controller.signal.aborted)
        this.opts.onError?.(error instanceof Error ? error : new Error('audio_failed'));
    });
  }
  stop(): void {
    this.controller.abort();
    if (ttsSupported && this.opts.speak) speechSynthesis.cancel();
  }
  private async speak(text: string): Promise<void> {
    const signal = this.controller.signal;
    signal.throwIfAborted();
    if (!this.opts.speak) return delay(estimateMs(text, this.opts.rate), signal);
    if (RECORDINGS[text]) return playRecording(text, this.opts.rate, signal);
    const voice = pickVoice(this.opts.voiceURI);
    if (!ttsSupported || !voice) throw new Error('english_voice_unavailable');
    await new Promise<void>((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = this.opts.rate;
      utterance.voice = voice;
      const cleanup = () => {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
        utterance.onend = null;
        utterance.onerror = null;
      };
      const abort = () => {
        cleanup();
        speechSynthesis.cancel();
        reject(new DOMException('Aborted', 'AbortError'));
      };
      const timer = setTimeout(
        () => {
          cleanup();
          speechSynthesis.cancel();
          reject(new Error('audio_interrupted'));
        },
        estimateMs(text, this.opts.rate) * 2 + 3000,
      );
      signal.addEventListener('abort', abort, { once: true });
      utterance.onend = () => {
        cleanup();
        resolve();
      };
      utterance.onerror = () => {
        cleanup();
        reject(new Error('audio_failed'));
      };
      speechSynthesis.speak(utterance);
    });
  }
  private async run(): Promise<void> {
    const signal = this.controller.signal;
    if (this.opts.speak) {
      await prepareRecordings(
        this.opts.items.flatMap((item) => [`${LETTERS[item.letterIndex]}.`, item.text]),
      );
    }
    for (const item of this.opts.items) {
      signal.throwIfAborted();
      this.opts.onItemStart?.(item.letterIndex);
      await this.speak(`${LETTERS[item.letterIndex]}.`);
      await delay(350, signal);
      await this.speak(item.text);
      await delay(this.opts.gapMs, signal);
    }
    signal.throwIfAborted();
    this.opts.onDone?.();
  }
}
export function warmVoices(): void {
  if (ttsSupported) speechSynthesis.getVoices();
}
